/**
 * Translation Pre-loading Service
 * 
 * This module handles pre-loading translations from Contentstack before React initializes.
 * It ensures translations are available for both the initial loading screen and React components.
 * 
 * This approach works in both standalone and AEM-embedded scenarios since it runs
 * from the JavaScript bundle rather than relying on index.html.
 */

import axios from 'axios';

/**
 * Apply translations to DOM elements with data-i18n-key attributes
 * Used for translating the initial loading screen before React mounts
 */
function applyTranslationsToDOM(translations: Record<string, string>): void {
  document.querySelectorAll<HTMLElement>('[data-i18n-key]').forEach((element) => {
    const key = element.dataset.i18nKey;
    if (key && translations[key]) {
      element.textContent = translations[key];
    }
  });
}

/**
 * Get the current locale from window.fsl_locale (set by AEM),
 * falling back to 'en-us'.
 */
function getLocale(): string {
  const locale = (window.fsl_locale ?? 'en-us').toLowerCase();
  console.log('[FSL Translations] Detected locale:', locale);
  return locale;
}

/**
 * Get the API URL from environment variables
 */
function getApiUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL;
  
  // Fallback to localhost if env var is not set or is a placeholder
  if (!apiUrl || apiUrl.startsWith('%')) {
    console.warn('[FSL Translations] VITE_API_URL not set, using localhost:3000');
    return 'http://localhost:3000';
  }
  
  return apiUrl;
}

/**
 * Mark translations as ready and dispatch event for listeners
 */
function markTranslationsReady(): void {
  window.__FSL_TRANSLATIONS_READY__ = true;
  window.dispatchEvent(new Event('fslTranslationsReady'));
  console.log('[FSL Translations] Marked as ready, event dispatched');
}

/**
 * Initialize and pre-load translations from Contentstack
 * 
 * This function:
 * 1. Fetches translations for the current locale from the backend
 * 2. Stores them in window.__FSL_TRANSLATIONS__ for React components
 * 3. Applies translations to any DOM elements with data-i18n-key attributes
 * 4. Sets a ready flag and dispatches an event when complete
 * 5. Has a 5-second timeout to prevent blocking app initialization
 * 
 * @returns Promise that resolves when translations are loaded or timeout occurs
 */
export async function initializeTranslations(): Promise<void> {
  console.log('[FSL Translations] Initialization started');
  
  // Initialize window globals
  window.__FSL_TRANSLATIONS_READY__ = false;
  window.__FSL_TRANSLATIONS__ = {};
  
  const locale = getLocale();
  const apiUrl = getApiUrl();
  const translationsUrl = `${apiUrl}/contentstack/translations/${locale}`;
  
  console.log('[FSL Translations] Fetching from:', translationsUrl);
  
  // Create timeout promise (5 seconds)
  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(() => {
      if (!window.__FSL_TRANSLATIONS_READY__) {
        console.warn('[FSL Translations] Timeout reached (5s) - proceeding without translations');
        markTranslationsReady();
      }
      resolve();
    }, 5000);
  });
  
  // Create fetch promise
  const fetchPromise = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4500); // Abort slightly before timeout
      
      const response = await axios.get<Record<string, string>>(translationsUrl, {
        signal: controller.signal,
        headers: { 
          'Accept': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);
      
      const translations = response.data;
      
      if (!translations || typeof translations !== 'object') {
        throw new Error('Invalid translation data received');
      }
      
      const translationCount = Object.keys(translations).length;
      console.log(`[FSL Translations] Successfully loaded ${translationCount} translations`);
      
      // Store in window for React components
      window.__FSL_TRANSLATIONS__ = translations;
      
      // Apply to any existing DOM elements (loading screen)
      applyTranslationsToDOM(translations);
      
      // Mark as ready
      markTranslationsReady();
      
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
          console.error('[FSL Translations] Request aborted (timeout)');
        } else {
          console.error('[FSL Translations] Failed to load:', error.message);
        }
      } else if (error instanceof Error) {
        console.error('[FSL Translations] Failed to load:', error.message);
      } else {
        console.error('[FSL Translations] Failed to load:', error);
      }
      
      // Mark as ready even on error to prevent blocking
      markTranslationsReady();
    }
  })();
  
  // Race between fetch and timeout
  await Promise.race([fetchPromise, timeoutPromise]);
  
  console.log('[FSL Translations] Initialization complete');
}
